import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

import './HealthStatusDialog.scss';

const StatusBadge = ({ status }) => {
  const statusClass = status === 'UP' ? 'status-up' : 'status-down';
  return <span className={`status-badge ${statusClass}`}>{status || 'UNKNOWN'}</span>;
};

StatusBadge.propTypes = {
  status: PropTypes.string,
};

const HealthCard = ({ title, data, isLoading }) => {
  if (isLoading) {
    return (
      <div className="health-card">
        <h4>{title}</h4>
        <div className="health-card-loading">Loading...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="health-card">
        <h4>{title}</h4>
        <div className="health-card-error">No data available</div>
      </div>
    );
  }

  return (
    <div className="health-card">
      <h4>
        {title} <StatusBadge status={data.status} />
      </h4>
      {data.error && <div className="health-card-error">Error: {data.error}</div>}
      {data.components && (
        <div className="health-components">
          {Object.entries(data.components).map(([name, component]) => (
            <div key={name} className="health-component">
              <span className="component-name">{name}</span>
              <StatusBadge status={component.status} />
              {component.details && (
                <pre className="component-details">
                  {JSON.stringify(component.details, null, 2)}
                </pre>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

HealthCard.propTypes = {
  title: PropTypes.string.isRequired,
  data: PropTypes.shape({
    status: PropTypes.string,
    error: PropTypes.string,
    components: PropTypes.object,
  }),
  isLoading: PropTypes.bool,
};

const HealthStatusDialog = ({
  isOpen,
  onClose,
  getComponent,
  backendSpecsActions,
  backendSpecsSelectors,
}) => {
  const Modal = getComponent('Modal', true);
  const ModalHeader = getComponent('ModalHeader', true);
  const ModalTitle = getComponent('ModalTitle', true);
  const ModalBody = getComponent('ModalBody', true);
  const ModalFooter = getComponent('ModalFooter', true);

  const health = backendSpecsSelectors?.selectHealth?.();
  const isLoading = backendSpecsSelectors?.selectHealthLoading?.() || false;
  const error = backendSpecsSelectors?.selectHealthError?.();

  useEffect(() => {
    if (isOpen && backendSpecsActions?.fetchHealthStatus) {
      backendSpecsActions.fetchHealthStatus();
    }
  }, [isOpen, backendSpecsActions]);

  const handleRefresh = useCallback(() => {
    if (backendSpecsActions?.fetchHealthStatus) {
      backendSpecsActions.fetchHealthStatus();
    }
  }, [backendSpecsActions]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!isOpen || !Modal) return null;

  return (
    <Modal isOpen={isOpen} contentLabel="Health Status">
      {ModalHeader && ModalTitle && (
        <ModalHeader>
          <ModalTitle>Server Health Status</ModalTitle>
        </ModalHeader>
      )}
      {ModalBody && (
        <ModalBody>
          <div className="health-status-container">
            {error && (
              <div className="health-error-banner">Error fetching health status: {error}</div>
            )}
            <div className="health-grid">
              <HealthCard title="Overall Health" data={health?.overall} isLoading={isLoading} />
              <HealthCard title="Readiness" data={health?.ready} isLoading={isLoading} />
              <HealthCard title="Liveness" data={health?.live} isLoading={isLoading} />
            </div>
            {health?.timestamp && (
              <div className="health-timestamp">
                Last updated: {new Date(health.timestamp).toLocaleString()}
              </div>
            )}
          </div>
        </ModalBody>
      )}
      {ModalFooter && (
        <ModalFooter>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleRefresh}
            disabled={isLoading}
          >
            {isLoading ? 'Refreshing...' : 'Refresh'}
          </button>
          <button type="button" className="btn btn-primary" onClick={handleClose}>
            Close
          </button>
        </ModalFooter>
      )}
    </Modal>
  );
};

HealthStatusDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  getComponent: PropTypes.func.isRequired,
  backendSpecsActions: PropTypes.shape({
    fetchHealthStatus: PropTypes.func,
  }),
  backendSpecsSelectors: PropTypes.shape({
    selectHealth: PropTypes.func,
    selectHealthLoading: PropTypes.func,
    selectHealthError: PropTypes.func,
  }),
};

HealthStatusDialog.defaultProps = {
  backendSpecsActions: null,
  backendSpecsSelectors: null,
};

export default HealthStatusDialog;
